import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useScrollHeader } from '../../hooks/useScrollHeader';
import { useHeroVisibility } from '../../hooks/useHeroVisibility';
import { NAV_LINKS } from '../../constants';
import { getDocsUrl } from '../../../../utils/docsUrl';
import { useUIStore } from '../../../../stores/uiStore';
import logo from '../../../../../assets/logo.png';

/**
 * Landing page header with transparent-to-solid background on scroll
 * Includes responsive mobile menu
 */
export const LandingHeader: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isScrolled: isScrolledDefault } = useScrollHeader({ threshold: 50 });
  const { isHeroVisible } = useHeroVisibility();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { currentTheme, setTheme } = useUIStore();

  // On mobile homepage, use hero visibility for header styling (consolidated timing)
  // On other pages or desktop, use default scroll threshold
  const isScrolled = isMobile && isHomePage ? !isHeroVisible : isScrolledDefault;

  // Login is enabled in all environments
  const isLoginDisabled = false;

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleThemeToggle = () => {
    setTheme(currentTheme === 'light' ? 'dark' : 'light');
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={isScrolled ? 2 : 0}
        sx={{
          bgcolor: isScrolled ? 'background.paper' : 'transparent',
          transition: theme.transitions.create(['background-color', 'box-shadow'], {
            duration: theme.transitions.duration.short,
          }),
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Mobile Menu Button - Left side */}
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuToggle}
                sx={{ color: 'text.primary', mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo - hidden on mobile homepage when hero logo is visible */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: isMobile ? 0 : 4,
                opacity: isMobile && isHomePage && !isScrolled ? 0 : 1,
                pointerEvents: isMobile && isHomePage && !isScrolled ? 'none' : 'auto',
                transition: theme.transitions.create('opacity', {
                  duration: theme.transitions.duration.short,
                }),
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Lecturo"
                sx={{
                  height: { xs: 28, md: 40 },
                  width: { xs: 28, md: 40 },
                  mr: 1,
                }}
              />
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Lecturo
              </Box>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                {NAV_LINKS.map((link) =>
                  link.external ? (
                    <Button
                      key={link.label}
                      component="a"
                      href={getDocsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem !important' }} />}
                      sx={{
                        color: 'text.primary',
                        mx: 1,
                        fontWeight: 400,
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      {link.label}
                    </Button>
                  ) : (
                    <Button
                      key={link.path}
                      component={link.disabled ? 'button' : RouterLink}
                      to={link.disabled ? undefined : link.path}
                      disabled={link.disabled}
                      sx={{
                        color: link.disabled ? 'text.disabled' : 'text.primary',
                        mx: 1,
                        fontWeight: isActive(link.path) ? 600 : 400,
                        position: 'relative',
                        cursor: link.disabled ? 'not-allowed' : 'pointer',
                        '&::after':
                          isActive(link.path) && !link.disabled
                            ? {
                                content: '""',
                                position: 'absolute',
                                bottom: 4,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '60%',
                                height: 2,
                                bgcolor: 'primary.main',
                                borderRadius: 1,
                              }
                            : {},
                        '&:hover': {
                          bgcolor: link.disabled ? 'transparent' : 'action.hover',
                        },
                        '&.Mui-disabled': {
                          color: 'text.disabled',
                        },
                      }}
                    >
                      {link.label}
                    </Button>
                  )
                )}
              </Box>
            )}

            {/* Spacer for mobile */}
            {isMobile && <Box sx={{ flexGrow: 1 }} />}

            {/* Theme Toggle Button */}
            <IconButton
              onClick={handleThemeToggle}
              size="small"
              sx={{
                color: 'text.primary',
                mr: 2,
              }}
              aria-label="toggle theme"
            >
              {currentTheme === 'dark' ? (
                <DarkModeIcon fontSize="small" />
              ) : (
                <LightModeIcon fontSize="small" />
              )}
            </IconButton>

            {/* Login Link - always visible on both mobile and desktop */}
            <Button
              component={isLoginDisabled ? 'button' : RouterLink}
              to={isLoginDisabled ? undefined : '/login'}
              variant="text"
              disabled={isLoginDisabled}
              startIcon={<LoginIcon />}
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                fontSize: { xs: '0.875rem', md: '1rem' },
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'primary.dark',
                },
                '&.Mui-disabled': {
                  color: theme.palette.action.disabled,
                },
              }}
            >
              Login
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        variant="temporary"
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          zIndex: theme.zIndex.drawer + 2, // Higher than AppBar
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header with Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              pt: 3,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Lecturo"
              sx={{
                height: 40,
                width: 40,
              }}
            />
          </Box>

          {/* Theme Toggle Button */}
          <IconButton
            onClick={handleThemeToggle}
            size="small"
            sx={{
              position: 'absolute',
              left: theme.spacing(1),
              top: theme.spacing(1),
            }}
            aria-label="toggle theme"
          >
            {currentTheme === 'dark' ? (
              <DarkModeIcon fontSize="small" />
            ) : (
              <LightModeIcon fontSize="small" />
            )}
          </IconButton>

          <IconButton
            onClick={handleMobileMenuClose}
            sx={{
              position: 'absolute',
              right: theme.spacing(1),
              top: theme.spacing(1),
              width: 48,
              height: 48,
            }}
          >
            <CloseIcon />
          </IconButton>

          <List sx={{ px: 1 }}>
            {NAV_LINKS.map((link) => (
              <ListItem key={link.external ? link.label : link.path} disablePadding>
                {link.external ? (
                  <ListItemButton
                    component="a"
                    href={getDocsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleMobileMenuClose}
                    sx={{
                      py: 1.5,
                      minHeight: 48,
                      cursor: 'pointer',
                      '&:active': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {link.label}
                          <OpenInNewIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                        </Box>
                      }
                      primaryTypographyProps={{
                        fontWeight: 400,
                        fontSize: '1rem',
                      }}
                    />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    component={link.disabled ? 'div' : RouterLink}
                    to={link.disabled ? undefined : link.path}
                    onClick={link.disabled ? undefined : handleMobileMenuClose}
                    selected={isActive(link.path) && !link.disabled}
                    disabled={link.disabled}
                    sx={{
                      py: 1.5,
                      minHeight: 48,
                      cursor: link.disabled ? 'not-allowed' : 'pointer',
                      '&.Mui-disabled': {
                        opacity: 0.5,
                      },
                      '&:active': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{
                        fontWeight: isActive(link.path) ? 600 : 400,
                        color: link.disabled ? 'text.disabled' : 'inherit',
                        fontSize: '1rem',
                      }}
                    />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>

          <Box sx={{ px: 3, pb: 2, mt: 'auto' }}>
            <Button
              component={isLoginDisabled ? 'button' : RouterLink}
              to={isLoginDisabled ? undefined : '/login'}
              variant="contained"
              fullWidth
              disabled={isLoginDisabled}
              onClick={isLoginDisabled ? undefined : handleMobileMenuClose}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: 'white',
                py: 1.5,
                fontWeight: 600,
                '&.Mui-disabled': {
                  background: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              Login
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Spacer to prevent content from being hidden behind fixed header */}
      <Toolbar />
    </>
  );
};
